<script setup>
import { ref, computed, onBeforeUnmount } from 'vue'
import { Check, Copy } from 'lucide-vue-next'
import { cn } from '../../../lib/utils'

const props = defineProps({
  // The value shown and copied to the clipboard.
  value: { type: [String, Number], default: '' },
  // Max width of the truncated text, in pixels. Injected into the inline style.
  maxWidth: { type: [String, Number], default: 120 },
  // Render the value in a monospace font (ids usually read better mono).
  mono: { type: Boolean, default: true },
  // How long the "copied" check stays visible, in ms.
  copiedDuration: { type: Number, default: 1500 },
  // Optional router-link target. When set, the value becomes a link to the detail page.
  to: { type: [String, Object], default: null },
  class: { type: null, required: false },
})

const copied = ref(false)
let timer = null

const textStyle = computed(() => {
  const px = typeof props.maxWidth === 'number' ? `${props.maxWidth}px` : props.maxWidth
  return { maxWidth: px }
})

async function copy() {
  try {
    await navigator.clipboard.writeText(String(props.value))
    copied.value = true
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      copied.value = false
    }, props.copiedDuration)
  } catch (e) {
    /* clipboard unavailable */
  }
}

onBeforeUnmount(() => {
  if (timer) clearTimeout(timer)
})
</script>

<template>
  <div :class="cn('flex items-center gap-1.5', props.class)" data-slot="copy-id">
    <component
      :is="to ? 'router-link' : 'span'"
      :to="to || undefined"
      class="truncate text-xs"
      :class="[
        mono ? 'font-mono' : '',
        to
          ? 'text-primary underline-offset-2 hover:underline'
          : 'text-muted-foreground',
      ]"
      :style="textStyle"
      :title="String(value)"
    >
      {{ value }}
    </component>
    <button
      type="button"
      class="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
      :title="copied ? 'Copied' : 'Copy ID'"
      @click.stop="copy"
    >
      <component :is="copied ? Check : Copy" class="size-3.5" />
    </button>
  </div>
</template>
